import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { PLATFORM_ID, Inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  DataService,
  PamlabApiServiceService,
  RouteService,
  UtilsService,
  AuthService,
} from '@/shared/services';
import { isPlatformBrowser } from '@angular/common';
import { map, startWith, Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { StudentNameAndID } from '../../../../../server/types';
import { studentExistsValidator } from '@/shared/directives/custom-validators';

@Component({
  selector: 'student-form',
  templateUrl: './student-form.component.html',
  styleUrls: ['./student-form.component.css'],
  standalone: false,
})
export class StudentFormComponent implements OnInit, OnDestroy {
  // variable name studentGroup of type event emitter string
  @Output() studentGroup = new EventEmitter<string>();

  public env = environment;
  public loadingStudents: boolean = false;
  private subscription: Subscription = new Subscription();

  public recommender_type: string = '';

  public studentNamesAndIDs!: StudentNameAndID[];
  public studentNamesAndIDsFiltered!: StudentNameAndID[];
  public height!: string;
  public shouldShowClearBtn: boolean = true;

  // student form group
  public studentForm = new FormGroup({
    student: new FormControl('', [Validators.required]),
  });

  constructor(
    public dialog: MatDialog,
    public authService: AuthService,
    private _apiService: PamlabApiServiceService,
    private _dataService: DataService,
    private _routeService: RouteService,
    private _utilsService: UtilsService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  ngOnInit(): void {
    if (this.authService.getRole() === 'student') {
      this.studentForm.patchValue({
        student: this.authService.getStudentNameAndId(),
      });
      this.studentForm.get('student')?.disable();
      this.shouldShowClearBtn = false;
      this.studentSubmit();
    } else {
      this.loadingStudents = true;
      this._apiService.fetchStudentNamesAndIDs().subscribe((data) => {
        this.studentNamesAndIDs = data;
        this.studentNamesAndIDsFiltered = [...data];
        this.studentForm
          .get('student')!
          .setValidators([studentExistsValidator(data)]);
        this.loadingStudents = false;

        this.subscription.add(
          this.studentForm
            .get('student')!
            .valueChanges.pipe(
              startWith(''),
              map((value) => {
                this.studentNamesAndIDsFiltered =
                  this.studentNamesAndIDs.filter((student) =>
                    `${student.name}${student.uwf_id.toString()}`
                      .toLowerCase()
                      .includes(value!.toLowerCase()),
                  );

                if (this.studentNamesAndIDsFiltered.length < 4) {
                  this.height =
                    this.studentNamesAndIDsFiltered.length * 50 + 'px';
                } else {
                  this.height = '200px';
                }
              }),
            )
            .subscribe(),
        );
      });
    }

    this.loadStudent();
  }

  loadStudent() {
    if (isPlatformBrowser(this.platformId)) {
      const queryString = window.location.search;
      const currentUrl = window.location.href;
      const [newUrl, ..._rest] = currentUrl.split('?');
      history.replaceState({}, document.title, newUrl);

      if (queryString.length > 0) {
        const urlParams = new URLSearchParams(queryString);
        this.studentForm.setValue({
          student: urlParams.get('student')!,
        });

        this.studentSubmit();
      }
    }
  }

  // raise an event with value from the toggle button
  checkGroup(group: string) {
    this.studentGroup.emit(group);
  }

  studentSubmit() {
    // SetTimeout is to wait the student content component to render and to have the
    // data pending subscription setup before this component. This is important for other
    // components that redirect to the student profile page
    setTimeout(() => {
      this._dataService.setPendingData(true);
    });

    // Parse the student id from this format: Ex. John Doe (970123456)
    const student_id = this._utilsService.getStudentIdFromNameAndIdString(
      this.studentForm.value.student!,
    );

    // calling _apiService to get student information based on form inputs.
    this.subscription.add(
      this._apiService.getStudent({ student_id }).subscribe({
        next: (data) => {
          this._dataService.setData({ data });
          this._dataService.setPendingData(false);
        },
        error: (errorRes: HttpErrorResponse) => {
          if (errorRes.status === 401) {
            this._routeService.navigate(environment['login_url']);
          }
          this._dataService.setData({ error: errorRes.error });
          this._dataService.setPendingData(false);
        },
      }),
    );
  }

  ngOnDestroy(): void {
    setTimeout(() => {
      this.subscription.unsubscribe();
    }, 3000);
  }

  // close popup dialog after form is submitted
  closeDialog() {
    this.dialog.closeAll();
  }
}
